import { useContext, useEffect, useState } from "react";
import { Button, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { ToastContext } from "../../component/global/ToastContext";
import { contentStudioApi, errorMessage } from "../../features/content-studio/api";
import MediaPreview from "../../features/content-studio/MediaPreview";
import { CONTENT_STATUS } from "../../features/content-studio/constants";
import {
    formatCount,
    formatDateTime,
    targetLabel,
    typeKey,
} from "../../features/content-studio/helpers";
import "../../features/content-studio/styles.scss";

const companyName = (content) =>
    content.company_name || content.company?.name || "—";

export default function ContentReviewTab() {
    const { t, i18n } = useTranslation();
    const show = useContext(ToastContext);
    const locale = i18n.language === "fa" ? "fa-IR" : "en-US";
    const [query, setQuery] = useState("");
    const [rows, setRows] = useState([]);
    const [approved, setApproved] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busyId, setBusyId] = useState(null);
    const [notes, setNotes] = useState({});

    const load = () => {
        setLoading(true);
        const search = query || undefined;
        Promise.all([
            contentStudioApi.adminQueue({ name: search, page_size: 50 }),
            contentStudioApi.adminQueue({
                name: search,
                page_size: 50,
                status: CONTENT_STATUS.CONFIRMED,
            }),
        ])
            .then(([pending, published]) => {
                setRows(pending.rows || []);
                setApproved(published.rows || []);
            })
            .catch((error) =>
                show(errorMessage(error, t("OrgManagement.Error")), "danger")
            )
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const decide = (content, decision) => {
        const note = notes[content.id] || "";
        if (decision === "reject" && !note.trim()) {
            show(t("contentStudio.review.note"), "danger");
            return;
        }
        setBusyId(content.id);
        contentStudioApi
            .review(content.id, decision, note)
            .then((updated) => {
                const next = { ...content, ...updated };
                if (decision === "approve") {
                    setRows((current) =>
                        current.filter((row) => row.id !== content.id)
                    );
                    setApproved((current) => [next, ...current]);
                    show(t("contentStudio.review.approved"), "success");
                } else if (decision === "unapprove") {
                    setApproved((current) =>
                        current.filter((row) => row.id !== content.id)
                    );
                    setRows((current) => [next, ...current]);
                    show(t("contentStudio.review.unapproved"), "success");
                } else {
                    setRows((current) =>
                        current.filter((row) => row.id !== content.id)
                    );
                    show(t("contentStudio.review.rejected"), "success");
                }
            })
            .catch((error) =>
                show(errorMessage(error, t("OrgManagement.Error")), "danger")
            )
            .finally(() => setBusyId(null));
    };

    const renderPending = (content) => (
        <article key={content.id} className="review-item">
            <MediaPreview content={content} />
            <div className="review-copy">
                <h3>{content.name}</h3>
                <p>{content.description || t("contentStudio.noDescription")}</p>
                <p className="studio-muted">
                    {t("contentStudio.review.company")}: {companyName(content)} ·{" "}
                    {t(`contentStudio.type.${typeKey(content.content_type)}`)} ·{" "}
                    {targetLabel(content, t)} ·{" "}
                    {t("contentStudio.analytics.views")}{" "}
                    {formatCount(content.views_count, locale)}
                </p>
                <label htmlFor={`review-note-${content.id}`}>
                    {t("contentStudio.review.note")}
                </label>
                <textarea
                    id={`review-note-${content.id}`}
                    value={notes[content.id] || ""}
                    placeholder={t("contentStudio.review.noteHelp")}
                    onChange={(event) =>
                        setNotes((current) => ({
                            ...current,
                            [content.id]: event.target.value,
                        }))
                    }
                />
                <div className="review-actions">
                    <Button
                        variant="success"
                        disabled={busyId === content.id}
                        onClick={() => decide(content, "approve")}
                    >
                        {busyId === content.id ? (
                            <Spinner animation="border" size="sm" />
                        ) : (
                            t("contentStudio.review.approve")
                        )}
                    </Button>
                    <Button
                        variant="danger"
                        disabled={busyId === content.id}
                        onClick={() => decide(content, "reject")}
                    >
                        {t("contentStudio.review.reject")}
                    </Button>
                </div>
            </div>
        </article>
    );

    const renderApproved = (content) => (
        <article key={content.id} className="review-item review-item--approved">
            <MediaPreview content={content} />
            <div className="review-copy">
                <h3>{content.name}</h3>
                <p>{content.description || t("contentStudio.noDescription")}</p>
                <p className="studio-muted">
                    {t("contentStudio.review.company")}: {companyName(content)} ·{" "}
                    {t(`contentStudio.type.${typeKey(content.content_type)}`)} ·{" "}
                    {targetLabel(content, t)}
                </p>
                <p className="review-approved-date">
                    <span>{t("contentStudio.review.approvedAt")}</span>
                    <time dateTime={content.reviewed_at || undefined}>
                        {formatDateTime(content.reviewed_at, locale) || "—"}
                    </time>
                </p>
                <div className="review-actions">
                    <Button
                        variant="outline-warning"
                        disabled={busyId === content.id}
                        onClick={() => decide(content, "unapprove")}
                    >
                        {busyId === content.id ? (
                            <Spinner animation="border" size="sm" />
                        ) : (
                            t("contentStudio.review.unapprove")
                        )}
                    </Button>
                </div>
            </div>
        </article>
    );

    return (
        <div
            id="org-panel-content-review"
            role="tabpanel"
            aria-labelledby="org-tab-content-review"
            className="org-content-review"
        >
            <div className="organizations_toolbar">
                <div>
                    <div className="organizations_title">
                        {t("contentStudio.review.title")}
                    </div>
                    <p className="studio-muted">{t("contentStudio.review.lead")}</p>
                </div>
            </div>
            <form
                className="review-toolbar"
                onSubmit={(event) => {
                    event.preventDefault();
                    load();
                }}
            >
                <input
                    className="organizations_search"
                    type="search"
                    value={query}
                    placeholder={t("contentStudio.review.search")}
                    onChange={(event) => setQuery(event.target.value)}
                />
                <Button variant="primary" type="submit">
                    {t("contentStudio.filters.apply")}
                </Button>
            </form>
            {loading ? (
                <div className="organizations_loading">
                    <Spinner animation="border" size="sm" /> {t("OrgManagement.Loading")}
                </div>
            ) : (
                <>
                    <section className="review-section" aria-labelledby="review-pending-heading">
                        <div className="review-section__head">
                            <h2 id="review-pending-heading">
                                {t("contentStudio.review.pendingTitle")}
                            </h2>
                            <span className="review-section__count">
                                {formatCount(rows.length, locale)}
                            </span>
                        </div>
                        {rows.length === 0 ? (
                            <div className="organizations_empty">
                                {t("contentStudio.review.empty")}
                            </div>
                        ) : (
                            <div className="review-list">{rows.map(renderPending)}</div>
                        )}
                    </section>
                    <section
                        className="review-section review-section--approved"
                        aria-labelledby="review-approved-heading"
                    >
                        <div className="review-section__head">
                            <h2 id="review-approved-heading">
                                {t("contentStudio.review.approvedTitle")}
                            </h2>
                            <span className="review-section__count">
                                {formatCount(approved.length, locale)}
                            </span>
                        </div>
                        <p className="studio-muted">
                            {t("contentStudio.review.approvedLead")}
                        </p>
                        {approved.length === 0 ? (
                            <div className="organizations_empty">
                                {t("contentStudio.review.approvedEmpty")}
                            </div>
                        ) : (
                            <div className="review-list">
                                {approved.map(renderApproved)}
                            </div>
                        )}
                    </section>
                </>
            )}
        </div>
    );
}
